import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'

export type UseInViewAnimationOptions = {
  /** When true, content is visible immediately (no observer). */
  disabled?: boolean
  threshold?: number
  rootMargin?: string
  /** Multiplied by `staggerIndex` (capped) for transition-delay when visible. */
  staggerMs?: number
  staggerIndex?: number
}

/**
 * Fade-up reveal when the element enters the viewport.
 * Respects `prefers-reduced-motion: reduce` (shows immediately).
 */
export function useInViewAnimation(opts: UseInViewAnimationOptions = {}): {
  ref: RefObject<HTMLDivElement | null>
  className: string
  style: CSSProperties
} {
  const ref = useRef<HTMLDivElement | null>(null)
  const [visible, setVisible] = useState(opts.disabled === true)

  useEffect(() => {
    if (opts.disabled) {
      setVisible(true)
      return
    }
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setVisible(true)
      return
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true)
      return
    }
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true)
            io.disconnect()
            break
          }
        }
      },
      { threshold: opts.threshold ?? 0.08, rootMargin: opts.rootMargin ?? '0px 0px -10% 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [opts.disabled, opts.threshold, opts.rootMargin])

  /** Keeps long pages snappy: delay only grows for the first N siblings. */
  const cap = 8
  const idx = opts.staggerIndex != null ? Math.max(0, Math.min(cap, opts.staggerIndex)) : 0
  const delayMs = visible && opts.staggerIndex != null ? idx * (opts.staggerMs ?? 50) : 0

  return {
    ref,
    className: visible ? 'sf-motion-reveal is-visible' : 'sf-motion-reveal',
    style: delayMs > 0 ? { transitionDelay: `${delayMs}ms` } : {},
  }
}
